import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { formatDate, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import type { Assessment, FunctionalTest } from 'modules/dashboard/core/type';

import type { AssessmentDetail, LearnerResult } from '../type';

import { differenceInMinutes, isSameMinute } from 'date-fns';
import { getTimeLine } from 'helpers';
import { apiCall } from 'hooks/service';

import { ASSESSMENT } from '../enum';

export const useAssessmentDetailController = () => {
  const location = useLocation();

  const navigationTo = useNavigate();

  const params = useParams();

  const { id } = params;

  const [activeKey, setActiveKey] = useState<string>(ASSESSMENT.RESULT);

  const [learnerResult, setLearnerResult] = useState<LearnerResult[]>([]);

  const [functionTests, setFunctionalTests] = useState<FunctionalTest[]>([]);

  const [assessment, setAssessment] = useState<Assessment>();

  const classScheduleId = location.state?.classScheduleId;

  const classLessonId = location.state?.classLessonId;

  useEffect(() => {
    if (!classScheduleId) {
      getAssessmentDetail();

      return;
    }

    getLessonDetail();

    getFunctionalTest();
  }, [classScheduleId, id]);

  const getLessonDetail = async () => {
    const result: AssessmentDetail = await apiCall({
      url: `/class-schedules/${classScheduleId}/learners`,
      method: 'GET',
      isLoading: true,
    });

    if (result?.result.length) {
      setLearnerResult(result.result);
    }

    if (result?.lesson) {
      setAssessment(result.lesson);

      const startDate = (result.lesson.start ? new Date(result.lesson.start) : '') as Date;

      const endDate = (result.lesson.end ? new Date(result.lesson.end) : '') as Date;

      const startTime = formatDate(startDate, 'HH:mm');

      const endTime = formatDate(endDate, 'HH:mm');

      setValues({
        start: startTime,
        end: endTime,
        date: result.lesson.start ?? '',
      });
    }
  };

  const getAssessmentDetail = async () => {
    const result: Assessment = await apiCall({
      url: `/assessments/${id}`,
      method: 'GET',
    });

    if (result?.id) {
      setAssessment(result);
      setFunctionalTests(result.functionalTests);
    }
  };

  const formik = useFormik({
    initialValues: {
      start: '',
      end: '',
      date: '',
    },

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      let result: any;

      if (classScheduleId) {
        result = await apiCall({
          url: `/class-schedules/${classScheduleId}`,
          method: 'PATCH',
          data: {
            start: getTimeLine(new Date(values.date), values.start).toISOString(),
            end: getTimeLine(new Date(values.date), values.end).toISOString(),
          },
          isLoading: true,
          showError: true,
        });
      } else {
        result = await apiCall({
          url: `/class-schedules`,
          method: 'POST',
          data: {
            data: [
              {
                classLessonId,
                start: getTimeLine(new Date(values.date), values.start).toISOString(),
                end: getTimeLine(new Date(values.date), values.end).toISOString(),
              },
            ],
          },
          isLoading: true,
          showError: true,
        });
      }

      if (result?.id || result?.length) {
        showToastMessage('success', 'Updated schedule successfully!');
      }
    },
  });

  const { values, handleSubmit, handleChange, isSubmitting, isValid, setValues, setFieldValue } =
    formik;

  const handleChangeDate = (value: Date) => {
    setFieldValue('date', value);
  };

  const disabled =
    !isValid ||
    isSubmitting ||
    differenceInMinutes(assessment?.start ?? '', assessment?.end ?? '') >= 0 ||
    (isSameMinute(assessment?.start ?? '', getTimeLine(new Date(values.date), values.start)) &&
      isSameMinute(assessment?.end ?? '', getTimeLine(new Date(values.date), values.end))) ||
    values.start > values.end;

  const getFunctionalTest = async () => {
    const result: FunctionalTest[] = await apiCall({
      url: `/class-schedules/${classScheduleId}/lessons-or-assessments`,
      method: 'GET',
    });

    if (result.length) {
      setFunctionalTests(result);
    }
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const goBack = () => {
    navigationTo(-1);
  };

  return {
    activeKey,
    onTabClick,
    learnerResult,
    functionTests,
    assessment,
    values,
    handleSubmit,
    handleChange,
    disabled,
    handleChangeDate,
    goBack,
  };
};
